import type { SubjectInputId, SubjectWithOptionalFee } from 'venn-state';
import {
  blockSettings,
  subjectGroupRequestSubjects,
  blockRequestSubjects,
  blockSubjects,
  analysisSubjects,
} from 'venn-state';
import { constSelector, useRecoilValue } from 'recoil';
import type { AnalysisSubject, CustomizableBlockSetting } from 'venn-utils';
import { getBlockSubjectTypeName, plural } from 'venn-utils';
import { compact, some } from 'lodash';
import { useMemo } from 'react';

const emptySelector = constSelector([]);
const EMPTY_ARRAY: ValidationError[] = [];

export type ValidationError =
  | {
      type: 'ERROR';
      problem: 'MISSING_SUBJECT' | 'CONTAINS_HISTORICAL_PORTFOLIO';
      message: string;
    }
  | {
      type: 'WARNING';
      message: string;
    };

export const useSubjectGroupErrors = (blockId: string, inputId: SubjectInputId | undefined): ValidationError[] => {
  const groupSubjects = useRecoilValue(inputId ? subjectGroupRequestSubjects([blockId, inputId]) : emptySelector);
  const allSubjects = useRecoilValue(blockRequestSubjects(blockId));
  const unfilteredRawSubjects = useRecoilValue(blockSubjects(blockId));
  const unfilteredSubjects = useRecoilValue(analysisSubjects(unfilteredRawSubjects));
  const blockSetting = useRecoilValue(blockSettings(blockId));

  return useMemo(() => {
    if (!inputId) {
      return EMPTY_ARRAY;
    }

    return compact([validateSubjects({ groupSubjects, allSubjects, blockSetting, unfilteredSubjects })]);
  }, [inputId, groupSubjects, allSubjects, blockSetting, unfilteredSubjects]);
};

const validateSubjects = ({
  groupSubjects,
  allSubjects,
  blockSetting,
  unfilteredSubjects,
}: {
  groupSubjects: SubjectWithOptionalFee[];
  allSubjects: SubjectWithOptionalFee[];
  blockSetting: CustomizableBlockSetting;
  unfilteredSubjects: AnalysisSubject[];
}): ValidationError | undefined => {
  if (groupSubjects.length === 0) {
    if (some(unfilteredSubjects, (subject) => subject.isHistoricalPortfolio)) {
      return {
        type: 'ERROR',
        problem: 'CONTAINS_HISTORICAL_PORTFOLIO',
        message: 'This block does not support portfolios with historical allocations.',
      };
    }
    return {
      type: 'ERROR',
      problem: 'MISSING_SUBJECT',
      message: `Subject group does not contain any ${getBlockSubjectTypeName(blockSetting)}s.`,
    };
  }
  if (groupSubjects.length < blockSetting.requiredSubjects && allSubjects.length < blockSetting.requiredSubjects) {
    return {
      type: 'WARNING',
      message: `Subject group contains only ${groupSubjects.length} ${plural(groupSubjects.length, {
        1: getBlockSubjectTypeName(blockSetting),
        other: `${getBlockSubjectTypeName(blockSetting)}s`,
      })}.`,
    };
  }
  return undefined;
};
